import React, { useEffect, useState, useMemo } from 'react';
import { supabase } from '../../lib/supabase';

const SystemMessageBanner = () => {
  const [activeBanner, setActiveBanner] = useState(null);

  useEffect(() => {
    let isMounted = true;
    
    const fetchActiveBanner = async () => {
      try {
        const now = new Date().toISOString();
        
        const { data, error } = await supabase
          .from('system_message_banners')
          .select('*')
          .eq('is_active', true)
          .or(`start_time.is.null,start_time.lte.${now}`)
          .or(`end_time.is.null,end_time.gte.${now}`)
          .order('created_at', { ascending: false })
          .limit(1);

        if (error) throw error;
        
        if (isMounted) {
          setActiveBanner(data && data.length > 0 ? data[0] : null);
        }
      } catch (err) {
        console.error('Error fetching system message banner:', err);
      }
    };

    fetchActiveBanner();
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    if (activeBanner) {
      document.body.style.paddingTop = '32px';
    } else {
      document.body.style.paddingTop = '0px';
    }
    return () => { document.body.style.paddingTop = '0px'; };
  }, [activeBanner]);

  // Memoize the message rendering so we don't recalculate it per repeat
  const renderedMessage = useMemo(() => {
    if (!activeBanner) return null;
    let { message, blink_keyword } = activeBanner;

    // Replace standard &nbsp; and consecutive spaces with non-breaking spaces (\u00A0) to prevent browser collapsing
    if (message) {
      message = message
        .replace(/&nbsp;/g, '\u00A0')
        .replace(/ {2,}/g, (match) => '\u00A0'.repeat(match.length));
    }
    
    if (!blink_keyword || blink_keyword.trim() === '') {
      return <span>{message}</span>;
    }

    const parts = message.split(new RegExp(`(${blink_keyword})`, 'gi'));
    return (
      <>
        {parts.map((part, index) => {
          if (part.toLowerCase() === blink_keyword.toLowerCase()) {
            return <span key={index} className="animate-blink font-bold">{part}</span>;
          }
          return <span key={index}>{part}</span>;
        })}
      </>
    );
  }, [activeBanner]);

  if (!activeBanner) return null;

  const animationDuration = activeBanner.scroll_speed ? `${activeBanner.scroll_speed * 2}s` : '80s';
  const gapSpacing = activeBanner.gap_spacing != null ? activeBanner.gap_spacing : 64;
  
  // Calculate unbreakable space string based on pixel gap (approx 4px per space)
  const spaceCount = Math.max(1, Math.floor(gapSpacing / 4));
  const spacerString = '\u00A0'.repeat(spaceCount);

  return (
    <div 
      className="w-full overflow-hidden flex items-center shadow-sm relative z-[10000] border-b"
      style={{ 
        backgroundColor: activeBanner.bg_color || '#2563eb', 
        color: activeBanner.text_color || '#ffffff',
        height: 'auto',
        minHeight: '24px'
      }}
    >
      {activeBanner.is_scrolling ? (
        <div 
          className="animate-marquee flex flex-nowrap py-1 items-center"
          style={{
            fontSize: activeBanner.font_size || '14px',
            fontWeight: '500',
            animationDuration: animationDuration,
            width: 'max-content'
          }}
        >
          {/* We only need 4-6 repeats to fill a 4K screen if the message is decent length, saving 100+ DOM nodes */}
          <div className="flex shrink-0">
            {[...Array(6)].map((_, i) => (
              <React.Fragment key={`a-${i}`}>
                <div className="flex-shrink-0 whitespace-nowrap">
                  {renderedMessage}
                </div>
                <div className="flex-shrink-0 whitespace-nowrap">
                  {spacerString}
                </div>
              </React.Fragment>
            ))}
          </div>
          <div className="flex shrink-0">
            {[...Array(6)].map((_, i) => (
              <React.Fragment key={`b-${i}`}>
                <div className="flex-shrink-0 whitespace-nowrap">
                  {renderedMessage}
                </div>
                <div className="flex-shrink-0 whitespace-nowrap">
                  {spacerString}
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      ) : (
        <div 
          className="w-full py-1 px-4 text-center whitespace-nowrap"
          style={{ fontSize: activeBanner.font_size || '14px', fontWeight: '500' }}
        >
          {renderedMessage}
        </div>
      )}
      <style dangerouslySetInnerHTML={{__html: `
        @keyframes marquee {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        .animate-marquee {
          will-change: transform;
          animation-name: marquee;
          animation-timing-function: linear;
          animation-iteration-count: infinite;
        }
        .animate-marquee:hover {
          animation-play-state: paused;
        }
        @keyframes custom-blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
        .animate-blink {
          animation: custom-blink 1.5s step-end infinite;
        }
      `}} />
    </div>
  );
};

export default SystemMessageBanner;